import { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [pwd, setPwd] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pwd.length < 8) { setErr("Password must be at least 8 characters."); return; }
    if (pwd !== confirm) { setErr("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset", { token, new_password: pwd });
      setDone(true);
      setTimeout(() => navigate("/login", { replace: true }), 2200);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-hh-cream flex items-center justify-center px-6 py-12" data-testid="reset-page">
      <div className="hh-card w-full max-w-md p-10">
        {!token ? (
          <div className="text-center">
            <h1 className="font-serif text-2xl text-hh-ink mb-2">Missing reset link</h1>
            <p className="text-sm text-hh-ink2">Please use the link from your email, or{" "}
              <Link to="/forgot" className="text-hh-primary hover:underline" data-testid="reset-restart">request a new one</Link>.
            </p>
          </div>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <span className="inline-grid place-items-center h-14 w-14 rounded-full bg-hh-sage/15 text-hh-sage mb-5">
              <CheckCircle2 size={22} />
            </span>
            <h1 className="font-serif text-2xl text-hh-ink mb-2">Password updated</h1>
            <p className="text-sm text-hh-ink2">Taking you back to sign in…</p>
          </div>
        ) : (
          <>
            <Link to="/login" className="text-xs text-hh-ink2 hover:text-hh-ink inline-flex items-center gap-1 mb-8">
              <ArrowLeft size={12} /> Back to sign in
            </Link>
            <p className="hh-overline mb-3">Author portal</p>
            <h1 className="font-serif text-3xl text-hh-ink mb-3">Choose a new password</h1>
            <p className="text-sm text-hh-ink2 mb-8 leading-relaxed">
              Make it at least 8 characters. Use whatever you&rsquo;ll actually remember.
            </p>
            <form onSubmit={onSubmit} className="space-y-5">
              <div>
                <label className="block text-sm text-hh-ink2 mb-2" htmlFor="pwd">New password</label>
                <PasswordInput
                  id="pwd" required minLength={8} autoComplete="new-password"
                  value={pwd} onChange={(e) => setPwd(e.target.value)}
                  data-testid="reset-new-password"
                  className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40 focus:border-hh-primary transition-all"
                />
              </div>
              <div>
                <label className="block text-sm text-hh-ink2 mb-2" htmlFor="confirm">Confirm password</label>
                <PasswordInput
                  id="confirm" required minLength={8} autoComplete="new-password"
                  value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  data-testid="reset-confirm-password"
                  className="w-full px-4 py-3 rounded-lg border border-hh-border bg-white text-hh-ink focus:outline-none focus:ring-2 focus:ring-hh-primary/40 focus:border-hh-primary transition-all"
                />
              </div>
              {err && (
                <div data-testid="reset-error" className="text-sm text-hh-danger bg-[#FBEAE6] border border-[#F1CEC5] rounded-lg px-4 py-3">{err}</div>
              )}
              <button type="submit" disabled={loading} data-testid="reset-submit" className="hh-btn-primary w-full disabled:opacity-60">
                {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Save new password
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
